// src/features/student-management/ui/organisms/PersonForm/personFormHelpers.ts
import type { FieldErrors } from 'react-hook-form';

import { normalizeRG } from '@/shared/utils/rg';
import { applyProgressiveCpfMask } from '@/shared/utils/string';

import type { Person } from '../../../types/person';
import type { StepKey } from './personFormConfig';
import { FIELD_TO_STEP } from './personFormConfig';
import type { PersonFormValues } from './personFormSchema';

export type StepSaveStatus = 'idle' | 'loading' | 'success' | 'error';

export function extractErrorMessage(err: unknown): string | null {
  if (err !== null && typeof err === 'object') {
    const e = err as {
      response?: { data?: { message?: unknown } };
      message?: unknown;
    };
    const msg = e.response?.data?.message ?? e.message;
    if (typeof msg === 'string' && msg.length > 0) return msg;
  }
  return null;
}

export function stepHasDirtyFields(step: StepKey, dirty: Record<string, unknown>): boolean {
  return Object.entries(FIELD_TO_STEP).some(([field, s]) => {
    if (s !== step) return false;
    const v = dirty[field];
    if (typeof v === 'boolean') return v;
    if (v !== null && typeof v === 'object') {
      return Object.values(v as Record<string, unknown>).some(x => x === true);
    }
    return false;
  });
}

export function countErrorsByStep(errors: FieldErrors<PersonFormValues>): Record<StepKey, number> {
  const counters: Record<StepKey, number> = {
    personal: 0,
    contact: 0,
    professional: 0,
    documents: 0,
    addresses: 0,
    profiles: 0,
    financial: 0,
    roles: 0,
  };
  for (const key of Object.keys(errors)) {
    const step = FIELD_TO_STEP[key];
    if (step !== undefined) counters[step] += 1;
  }
  return counters;
}

/** Retorna os valores iniciais do formulário a partir de uma pessoa existente (edição) ou vazio (criação) */
export function getPersonFormDefaultValues(person?: Person): PersonFormValues {
  return {
    firstName: person?.firstName ?? '',
    lastName: person?.lastName ?? '',
    alias: person?.alias ?? '',
    email: person?.email ?? '',
    cpf: applyProgressiveCpfMask(person?.cpf ?? ''),
    birthDate: person?.birthDate ?? null,
    gender: (person?.gender as 1 | 2 | 3 | undefined) ?? undefined,
    martialStatus: person?.martialStatus ?? undefined,
    nationalityId: person?.nationalityId ?? undefined,
    birthCountryId: person?.birthCountryId ?? undefined,
    birthStateId: person?.birthStateId ?? undefined,
    birthCityId: person?.birthCityId ?? undefined,
    birthStateName: person?.birthStateName ?? null,
    birthCityName: person?.birthCityName ?? null,
    mothersName: person?.mothersName ?? '',
    fathersName: person?.fathersName ?? '',
    spouseName: person?.spouseName ?? '',
    celPhone: person?.celPhone ?? '',
    homePhone: person?.homePhone ?? '',
    workPhone: person?.workPhone ?? '',
    receiveSms: person?.receiveSms ?? false,
    receiveMarketing: person?.receiveMarketing ?? false,
    miniCurriculum: person?.miniCurriculum ?? '',
    urlLinkedin: person?.urlLinkedin ?? '',
    urlLattes: person?.urlLattes ?? '',
    observation: person?.observation ?? '',
    rg: normalizeRG(person?.rg ?? ''),
    dispatcherOrgan: person?.dispatcherOrgan ?? '',
    dispatcherOrganState: person?.dispatcherOrganState ?? '',
    dateDispatch: person?.dateDispatch ?? null,
    passportNumber: person?.passportNumber ?? '',
    idCountryNumber: person?.idCountryNumber ?? '',
    profession: person?.profession ?? '',
    professionId: person?.professionId ?? undefined,
    company: person?.company ?? '',
    associatedCompanyId: person?.associatedCompanyId ?? undefined,
    jobPositionId: person?.jobPositionId ?? undefined,
    otherJobPosition: person?.otherJobPosition ?? '',
    businessSectorId: person?.businessSectorId ?? undefined,
    otherBusinessSector: person?.otherBusinessSector ?? '',
    serviceTime: person?.serviceTime ?? undefined,
    experienceLevel: person?.experienceLevel ?? undefined,
    selfEmployed: person?.selfEmployed ?? false,
    isWorking: person?.isWorking ?? false,
    academicEducation: person?.academicEducation ?? undefined,
    academicInstitutionId: person?.academicInstitutionId ?? undefined,
    academicCourseName: person?.academicCourseName ?? '',
    completionDate: person?.completionDate ?? null,
    isStudent: person?.isStudent ?? false,
    isEmployee: person?.isEmployee ?? false,
    isTeachingStaff: person?.isTeacher ?? false,
    isMonitor: person?.isMonitor ?? false,
    student: {
      ra: person?.student?.ra ?? null,
      curriculum: person?.student?.curriculum ?? null,
      codeUsp: person?.student?.codeUsp ?? null,
      perfilPublic: person?.student?.perfilPublic ?? false,
    },
    employee: {
      postId: person?.employee?.postId ?? null,
      codeBadge: person?.employee?.codeBadge ?? null,
      dateStart: person?.employee?.dateStart ?? null,
      dateFinish: person?.employee?.dateFinish ?? null,
      type: person?.employee?.type ?? null,
      serviceRequestNotification: person?.employee?.serviceRequestNotification ?? false,
    },
    teachingStaff: {
      observation: person?.teachingStaff?.observation ?? null,
      isUspTeaching: person?.teachingStaff?.isUspTeaching ?? false,
      codeUSP: person?.teachingStaff?.codeUSP ?? null,
      certNumber: person?.teachingStaff?.certNumber ?? null,
      uspContractType: person?.teachingStaff?.uspContractType ?? null,
      academicEducation: person?.teachingStaff?.academicEducation ?? null,
      certWorkload: person?.teachingStaff?.certWorkload ?? null,
      certExpirationDate: person?.teachingStaff?.certExpirationDate ?? null,
      isLattes: person?.teachingStaff?.isLattes ?? false,
      lattesUpdateDate: person?.teachingStaff?.lattesUpdateDate ?? null,
    },
    isPCD: person?.isPCD ?? false,
    pcdDescription: person?.pcdDescription ?? '',
    useAccessibilityTool: person?.useAccessibilityTool ?? false,
    accessibilityToolDescription: person?.accessibilityToolDescription ?? '',
    personalAddress: {
      street: person?.personalAddress?.street ?? '',
      zipCode: person?.personalAddress?.zipCode ?? '',
      placeNumber: person?.personalAddress?.placeNumber ?? '',
      neighborhood: person?.personalAddress?.neighborhood ?? '',
      complement: person?.personalAddress?.complement ?? '',
      countryId: person?.personalAddress?.countryId ?? 1,
      stateId: person?.personalAddress?.stateId ?? undefined,
      cityId: person?.personalAddress?.cityId ?? undefined,
      stateName: person?.personalAddress?.stateName ?? null,
      cityName: person?.personalAddress?.cityName ?? null,
    },
    paymentAddress: {
      street: person?.paymentAddress?.street ?? '',
      zipCode: person?.paymentAddress?.zipCode ?? '',
      placeNumber: person?.paymentAddress?.placeNumber ?? '',
      neighborhood: person?.paymentAddress?.neighborhood ?? '',
      complement: person?.paymentAddress?.complement ?? '',
      countryId: person?.paymentAddress?.countryId ?? 1,
      stateId: person?.paymentAddress?.stateId ?? undefined,
      cityId: person?.paymentAddress?.cityId ?? undefined,
      stateName: person?.paymentAddress?.stateName ?? null,
      cityName: person?.paymentAddress?.cityName ?? null,
    },
    financialBankName: person?.financial?.bankName ?? '',
    financialAgency: person?.financial?.agency ?? '',
    financialCheckingAccount: person?.financial?.checkingAccount ?? '',
    financialSavingAccount: person?.financial?.savingAccount ?? '',
    financialPaymentMethod: person?.financial?.paymentMethod ?? undefined,
    financialAccountType: person?.financial?.accountType ?? undefined,
  };
}
